<template>
  <div
    class="card"
    :data-test-id="production._id"
  >
    <div class="card__content">
      <div class="card__header">
        <div
          class="card__status-indicator"
          :class="`card__status-indicator--${production.connected ? 'connected' : 'disconnected'}`"
          :title="production.connected ? 'Active' : 'Inactive'"
        >
          {{ production.connected ? 'Active' : 'Inactive' }}
        </div>

        <div class="card__actions">
          <label
            v-if="!isDeletedTab"
            data-entity="producer-dashboard-starProduction"
            @click.prevent.stop="toggleStar"
            class="margin-right-sm"
          >
            <span class="tgb-sr-only">Star {{ production.title }}</span>

            <input
              type="checkbox"
              class="tgb-sr-only"
              :checked="production.flagged"
            >

            <span
              class="fa-star"
              :class="production.flagged ? 'fas' : 'far'"
            />
          </label>

          <dropdown position="bottom right" :classes="dropdownClasses">
            <template #trigger>
              <i
                class="fas fa-ellipsis-h tgb-icon-ellipsis"
                data-entity="producer-dashboard-productionContext"
              ></i>
            </template>

            <template #dropdown-body>
              <tgb-context-menu :dropDownMenu="true" :actions="menuItems">
                <template v-slot="action">
                  <button
                    class="tgbContextMenuButton"
                    :data-entity="`producer-dashboard-${action.key}`"
                    @click="dropDownMenuItemClicked(action.callbackName, action.trigger)"
                  >
                    <span
                      v-if="action.icon"
                      class="fas"
                      :class="action.icon"
                    />

                    {{ action.text }}
                  </button>
                </template>
              </tgb-context-menu>
            </template>
          </dropdown>
        </div>
      </div>

      <h2 class="card__title" :title="production.title">
        {{ production.title }}
      </h2>

      <!-- In Production + Team Row -->
      <div class="card__meta margin-bottom-md">
        <div v-if="!isDeletedTab" class="overflow-visible">
          <label class="card__meta-label">
            In Production
          </label>

          <tgb-avatar-list :users="usersInProduction" :max="2">
            <!-- Blank when no users -->
            <template #noUsers>
              <div class="card__empty-users-list" />
            </template>
          </tgb-avatar-list>
        </div>

        <div v-if="hasTeams" class="text-left flex flex-column">
          <label class="card__meta-label">
            Team
          </label>

          <img
            class="card__avatar"
            :src="ownedBy.avatar"
            :alt="ownedBy.displayName"
            @error="$event.target.src=fallbackAvatar"
          >
        </div>
      </div>

      <!-- Modified + Created Row -->
      <div class="card__meta">
        <div>
          <!-- Modified shows name + date only, no avatar -->
          <label class="card__meta-label">
            Modified
          </label>

          <div class="card__meta-text">
            {{ modifiedBy.displayName }}
          </div>

          <div class="card__meta-date">
            {{ modifiedAt }}
          </div>
        </div>

        <div
          class="text-left flex flex-column"
          :title="`${createdBy.displayName} - ${createdAt}`"
        >
          <!-- Created just shows the avatar and no name -->
          <label class="card__meta-label">
            Created
          </label>

          <img
            class="card__avatar"
            :src="createdBy.avatar"
            :alt="createdBy.displayName"
            @error="$event.target.src=fallbackAvatar"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';

import { DefaultOptions, DeletedOptions } from './constants';

import TgbAvatarList from '../user-avatar/TgbAvatarList.vue';
import TgbContextMenu from '../context-menu/TgbContextMenu.vue';

const FallbackAvatar = './producer/assets/img/profile_placeholder.png';

export default {
  name: 'DirectoryCard',

  components: {
    TgbAvatarList,
    TgbContextMenu,
  },

  props: {
    production: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(['hasTeams']),

    usersInProduction() {
      const { byProduction } = this.$store.state.clients;
      const users = byProduction[this.production._id] || [];
      return users;
    },

    /**
    * Metadata for created by user
    */
    createdBy() {
      const { createdBy } = this.production;

      return {
        ...createdBy,
        avatar: createdBy?.profileImageUrl || FallbackAvatar,
      };
    },

    createdAt() {
      const { created } = this.production;
      return created ? format(created, 'MM/dd/yyyy') : '';
    },

    /**
    * Metadata for owner
    */
    ownedBy() {
      const { ownedBy } = this.production;

      return {
        ...ownedBy,
        avatar: ownedBy?.profileImageUrl || FallbackAvatar,
      };
    },

    modifiedBy() {
      const { modifiedBy } = this.production;
      return modifiedBy || {};
    },

    modifiedAt() {
      const { modified } = this.production;
      return modified ? format(modified, 'MM/dd/yyyy') : '';
    },

    isDeletedTab() {
      return this.$route.params?.tab === 'deleted';
    },

    menuItems() {
      return this.isDeletedTab ? DeletedOptions : DefaultOptions;
    },
  },

  data() {
    return {
      fallbackAvatar: FallbackAvatar,
      dropdownClasses: 'dropdown-menu-w-a dropdown-menu-p-0',
    };
  },

  methods: {
    toggleStar() {
      this.$emit('dir-item-starred', this.production);
    },

    dropDownMenuItemClicked(callbackName, trigger) {
      this.$emit('dropdown-menu-item-clicked', { callbackName, trigger });
    },
  },
};
</script>

<style lang="scss" scoped>
// need some grid utility classes but can't do that without collision right now,
// so working away from that with some one off card grid css
.card {
  padding: 1.5rem;
  background-color: var(--tgb-surface-default-color); //these need a layer of abstraction
  //put a border on that matches the background to prevent shifting on focus
  border: 1px solid var(--tgb-surface-default-color);
  border-radius: 2px;
  margin: 0;

  // todo: this shouldn't be globally applied, but for now it's going here
  // it is part of the css needed to align the last item of the card to the bottom
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;

  &[aria-role='button'] {
    &:hover {
      background-color: var(--tgb-directory-card-hover); // new will be #1A2527;
      color: #23CDF4; // TODO: make var text secondary default
    }

    &.cardActive {
      background-color: var(--tgb-directory-card-hover); // new will be #1A2527;
      color: #23CDF4; // TODO: make var text secondary default
      border-color: #23CDF4;
    }
  }
}

.card__header {
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
}

.card__content {
  // todo: this shouldn't be globally applied, but for now it's going here
  // it is part of the css needed to align the last item of the card to the bottom
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.card__title {
  display: block;
  line-height: 1.5em;
  min-height: 3em; /* At least two lines long */
  font-family: Barlow, sans-serif; //todo: import barlow font from google
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  display: inline;
  overflow: hidden;
  word-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card__actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;

  > label {
    cursor: pointer;
  }

  .fas.fa-star {
    color: #23CDF4;
  }
}

.card__avatar {
  width: 28px;
  height: 28px;
  border-radius: 100%;
}

.card__meta {
  display: flex;

  > div {
    flex: 1 1 auto;
    overflow: hidden;

    &.overflow-visible {
      overflow: visible;
    }

    &:last-child:not(:first-child) {
      flex: 0 0 auto;
      min-width: 52px;
      margin-left: 1rem;
    }
  }
}

.card__meta-label {
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 150%;
  letter-spacing: 0.1em;
  color: #808689;
  margin-bottom: 0.25rem;
}

.card__status-indicator {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: calc(8px + 0.5rem);
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;

  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 100%;
    background-color: #00D483;
  }

  &--disconnected {
    &:before {
      background-color: #e65447;
    }
  }
}

.card__meta-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card__meta-date {
  font-size: 11px;
}

.card__empty-users-list {
  height: 32px;
}
</style>
